import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useLaunchWindowsConfig } from '../context/LaunchWindowsContext';
import {
  computeBannerState,
  formatReferenceLabel,
  playLaunchSound,
  SOUND_LEAD_SECONDS,
  STATE_COUNTDOWN,
} from '../utils/launchWindows';

/**
 * Banner de subidas de lotes: consome a config compartilhada (LaunchWindowsProvider)
 * e recomputa o estado (1: próxima janela, 2: contagem, 3: lançamento) a cada segundo.
 * O horário de referência é por loja (acesso antecipado para clube, liberação geral
 * caso contrário), então duas lojas podem estar em estados diferentes ao mesmo tempo.
 */
export const useLaunchWindowsBanner = () => {
  const authUser = useSelector(state => state.authentication?.user);
  const isLoggedIn = authUser?.isLoggedIn?.() ?? false;

  const benefitsClub = useSelector(state => {
    const email = state.user?.profile?.email ?? '';
    return email.endsWith('@instacarro.com') || (state.user?.profile?.benefitsClub ?? false);
  });

  const { days, loading } = useLaunchWindowsConfig();

  const [banner, setBanner] = useState({ active: false });

  useEffect(() => {
    let cancelled = false;

    const tick = () => {
      if (cancelled) {
        return;
      }
      setBanner(
        computeBannerState({
          days: days ?? [],
          benefitsClub,
          now: Date.now(),
        })
      );
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [days, benefitsClub]);

  // Alerta sonoro: toca uma única vez ao cruzar os 4s finais da contagem, e só para
  // quem está com a aba aberta nesse momento (comparamos com o tick anterior; em um
  // mount já dentro dos 4s não há tick anterior, então não toca retroativamente).
  const prevTickRef = useRef({ epoch: null, remaining: null });
  const audioRef = useRef(null);

  useEffect(() => {
    if (!banner.active || banner.state !== STATE_COUNTDOWN) {
      prevTickRef.current = { epoch: banner.referenceEpoch ?? null, remaining: null };
      return;
    }

    const prev = prevTickRef.current;
    const crossedIntoLead =
      prev.epoch === banner.referenceEpoch &&
      prev.remaining != null &&
      prev.remaining > SOUND_LEAD_SECONDS &&
      banner.remainingSeconds <= SOUND_LEAD_SECONDS &&
      banner.remainingSeconds > 0;

    if (crossedIntoLead) {
      playLaunchSound(audioRef);
    }

    prevTickRef.current = {
      epoch: banner.referenceEpoch,
      remaining: banner.remainingSeconds,
    };
  }, [banner]);

  return {
    active: isLoggedIn && Boolean(banner.active),
    loading,
    state: banner.state ?? null,
    lotName: banner.lotName ?? '',
    referenceLabel: banner.referenceStr ? formatReferenceLabel(banner.referenceStr) : '',
    remainingSeconds: banner.remainingSeconds ?? 0,
  };
};
